/**
 * AdminPlataformaPage — Consolidated Platform (Marketplace, Automações, Event Bus, Developers)
 */
import React, { useState, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Search, X, ChevronDown, Store, Zap, Radio } from 'lucide-react';
import { AdminConsoleLayout } from '../components/AdminConsoleLayout';
import { AdminMarketplacePage } from './AdminMarketplacePage';
import { AdminAutomationsPage } from './AdminAutomationsPage';
import { AdminEventBusPage } from './AdminEventBusPage';
import { AdminDeveloperPortalPage } from './AdminDeveloperPortalPage';
import { desktopRibbonInputClass } from '../../../components/ribbon/DesktopRibbonControls';
import type { RibbonAction } from '../../../types/ribbon';

export const AdminPlataformaPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const activeTab = searchParams.get('tab') ?? 'marketplace';
  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [eventTypeFilter, setEventTypeFilter] = useState('');
  const [showAddApp, setShowAddApp] = useState(false);
  const [showCreateAutomation, setShowCreateAutomation] = useState(false);

  const filterBtnClass = 'flex h-[30px] shrink-0 items-center gap-1.5 rounded-[3px] border border-[var(--border-default)] bg-[var(--bg-surface)] px-2 text-[12px] text-[var(--text-primary)] transition-colors hover:bg-[var(--button-hover)]';

  const contextActions = useMemo<RibbonAction[]>(() => {
    if (activeTab === 'marketplace') {
      return [{ id: 'add-app', label: 'App', icon: Store, onClick: () => setShowAddApp(true) }];
    }
    if (activeTab === 'automacoes') {
      return [{ id: 'new-automation', label: 'Automação', icon: Zap, onClick: () => setShowCreateAutomation(true) }];
    }
    return [];
  }, [activeTab]);

  const filtersWidget = useMemo(() => {
    if (activeTab === 'marketplace') {
      return (
        <div className="flex flex-1 items-center justify-end gap-1.5">
          <div className="relative">
            <select
              className={`${filterBtnClass} appearance-none cursor-pointer`}
              style={{ paddingLeft: 22, paddingRight: 22 }}
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value)}
            >
              <option value="all">Todas categorias</option>
              <option value="Automation">Automação</option>
              <option value="Analytics">Analytics</option>
              <option value="Integrations">Integrações</option>
              <option value="Productivity">Produtividade</option>
              <option value="Lean Tools">Lean Tools</option>
              <option value="Data Export">Exportação</option>
              <option value="AI">AI</option>
            </select>
            <Store size={12} className="pointer-events-none absolute left-2 top-1/2 -translate-y-1/2 text-[var(--text-secondary)]" />
            <ChevronDown size={10} className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-[var(--text-secondary)]" />
          </div>
          <div className="flex w-[220px] shrink-0 items-center">
            <div className="flex w-full items-center gap-2 rounded-[3px] border border-[var(--border-default)] bg-[var(--bg-surface)] px-2">
              <Search size={14} className="shrink-0 text-[var(--text-secondary)]" />
              <input
                type="text"
                placeholder="Buscar apps..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className={`w-full ${desktopRibbonInputClass} border-0 bg-transparent px-0`}
              />
              {search && (
                <button onClick={() => setSearch('')} className="shrink-0 rounded p-0.5 hover:bg-[var(--button-hover)]">
                  <X size={10} className="text-[var(--text-secondary)]" />
                </button>
              )}
            </div>
          </div>
        </div>
      );
    }
    if (activeTab === 'event-bus') {
      return (
        <div className="flex flex-1 items-center justify-end gap-1.5">
          <div className="relative">
            <select
              className={`${filterBtnClass} appearance-none cursor-pointer`}
              style={{ paddingLeft: 22, paddingRight: 22 }}
              value={eventTypeFilter}
              onChange={(e) => setEventTypeFilter(e.target.value)}
            >
              <option value="">Todos os tipos</option>
            </select>
            <Radio size={12} className="pointer-events-none absolute left-2 top-1/2 -translate-y-1/2 text-[var(--text-secondary)]" />
            <ChevronDown size={10} className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-[var(--text-secondary)]" />
          </div>
        </div>
      );
    }
    return null;
  }, [activeTab, categoryFilter, eventTypeFilter, search]);

  return (
    <AdminConsoleLayout ribbonSlots={{ afterGroups: filtersWidget }} contextActions={contextActions}>
      <div className="h-full overflow-auto">
        {activeTab === 'marketplace' && <AdminMarketplacePage externalSearch={search} externalCategory={categoryFilter} externalShowAdd={showAddApp} onCloseAdd={() => setShowAddApp(false)} />}
        {activeTab === 'automacoes' && <AdminAutomationsPage externalShowCreate={showCreateAutomation} onCloseCreate={() => setShowCreateAutomation(false)} />}
        {activeTab === 'event-bus' && <AdminEventBusPage externalFilterType={eventTypeFilter} />}
        {activeTab === 'developers' && <AdminDeveloperPortalPage />}
      </div>
    </AdminConsoleLayout>
  );
};
